/**
 * Acronis Source Form Component
 *
 * Renders Acronis-specific form fields:
 * - Category multi-select (Alert Manager, Event Manager, Topics)
 * - Topics multi-select (populated based on Event Manager selection)
 * - Topics loading/error states with credential blur handlers
 *
 * Integration:
 * - Works with React Hook Form (uses useFormContext)
 * - Conditionally rendered in SourceCreateForm when source type is ACRONIS
 * - Integrates with useAcronisTopicsLoader for credential-based topic loading
 * - Uses existing project components (DynamicFormControl, Grid, etc.)
 */

import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import DynamicFormControl from '@/components/DynamicFormControl';
import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import {
  acronisTopicsValidatorAtom,
  selectedSourceDetailsAtom
} from '../SourceCreate.store';
import type { DynamicFieldData } from '../SourceCreate.types';
import { SourceDialogMode } from '../SourceCreate.types';
import useStyles from './AcronisTopics.styles';
import type { AcronisTopic, AcronisTopicsProps } from './AcronisTopics.types';
import { AcronisCategoryOption } from './AcronisTopics.types';
import { useAcronisCredentialBlurHandler } from './hooks/useAcronisCredentialBlurHandler';
import { useAcronisTopicsLoader } from './hooks/useAcronisTopicsLoader';

const ACRONIS_CREDENTIAL_FIELDS = [
  'client_id',
  'client_secret',
  'base_url'
] as const;
const TOPICS_API_ERROR_MESSAGE =
  'Unable to load topics. Please verify the Base URL, Client ID and Client Secret.';

const NO_TOPICS: AcronisTopic[] = [];

/**
 * AcronisTopics encapsulates Acronis-specific form logic.
 *
 * Props:
 * @param mode - Dialog mode (New or Edit)
 * @param isResettingSourceType - Whether source type is being reset (hide form)
 *
 * Rendered in SourceCreateForm.tsx when selectedSourceTypeId === SOURCE_TYPE.ACRONIS
 */
const AcronisTopicsContent = ({ mode }: AcronisTopicsProps) => {
  const { formState, getValues, setValue, trigger, clearErrors, control } =
    useFormContext();
  const { errors, dirtyFields, submitCount } = formState;
  const { classes } = useStyles({});
  const { enqueueSnackbar } = useSnackbar();
  const [, setAcronisTopicsValidator] = useAtom(acronisTopicsValidatorAtom);
  const handleCredentialBlurRef = useRef<() => Promise<boolean>>(
    async () => false
  );
  const lastTopicsErrorRef = useRef<string | null>(null);

  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  // Load topics based on credential blur events
  const {
    topics,
    isLoading: isTopicsLoading,
    error: topicsError,
    hasSuccessfulTopicsResponse,
    handleCredentialBlur
  } = useAcronisTopicsLoader({
    sourceTypeId: SOURCE_TYPE.ACRONIS,
    mode
  });

  // Set up credential blur handler integration
  useAcronisCredentialBlurHandler({
    onCredentialBlur: handleCredentialBlur
  });

  useEffect(() => {
    handleCredentialBlurRef.current = handleCredentialBlur;
  }, [handleCredentialBlur]);

  useEffect(() => {
    setAcronisTopicsValidator(() => () => handleCredentialBlurRef.current());

    return () => {
      setAcronisTopicsValidator(null);
    };
  }, [setAcronisTopicsValidator]);

  // Watch category selection to conditionally show/hide Topics field
  const selectedCategories = useWatch({ control, name: 'category' }) || [];
  const baseUrlValue = useWatch({ control, name: 'base_url' });
  const clientIdValue = useWatch({ control, name: 'client_id' });
  const clientSecretValue = useWatch({ control, name: 'client_secret' });
  const eventManagerSelected = selectedCategories.includes(
    AcronisCategoryOption.EventManager
  );
  // Topics are only offered while Event Manager is selected
  const availableTopics: AcronisTopic[] = eventManagerSelected
    ? topics
    : NO_TOPICS;

  const selectedTopicValues = useWatch({ control, name: 'topics' }) || [];
  const normalizedSelectedTopicValues = useMemo<string[]>(
    () =>
      Array.isArray(selectedTopicValues)
        ? selectedTopicValues
            .map((topic) => {
              if (typeof topic === 'string') {
                return topic;
              }

              if (
                topic &&
                typeof topic === 'object' &&
                'value' in topic &&
                typeof topic.value === 'string'
              ) {
                return topic.value;
              }

              return '';
            })
            .filter(Boolean)
        : [],
    [selectedTopicValues]
  );

  const mergedTopicOptions = useMemo(() => {
    const optionMap = new Map<string, { label: string; value: string }>();

    availableTopics.forEach((topic: AcronisTopic) => {
      optionMap.set(topic.id, {
        label: topic.name || topic.id,
        value: topic.id
      });
    });

    normalizedSelectedTopicValues.forEach((savedTopic: string) => {
      if (topicsError) {
        return;
      }

      const existsInAvailableTopics = availableTopics.some(
        (availableTopic: AcronisTopic) =>
          availableTopic.id === savedTopic || availableTopic.name === savedTopic
      );

      if (!existsInAvailableTopics && !optionMap.has(savedTopic)) {
        optionMap.set(savedTopic, {
          label: `${savedTopic} - no longer available`,
          value: savedTopic
        });
      }
    });

    return Array.from(optionMap.values());
  }, [availableTopics, normalizedSelectedTopicValues, topicsError]);

  useEffect(() => {
    if (
      mode !== SourceDialogMode.Edit ||
      !eventManagerSelected ||
      topicsError ||
      dirtyFields?.topics
    ) {
      return;
    }

    const savedTopics =
      (selectedSourceDetails as { topics?: string[] } | undefined)?.topics ||
      [];
    if (savedTopics.length === 0) {
      return;
    }

    const currentTopics = normalizedSelectedTopicValues;
    const savedTopicsSorted = [...savedTopics].sort();
    const currentTopicsSorted = [...currentTopics].sort();
    const topicsMatch =
      savedTopicsSorted.length === currentTopicsSorted.length &&
      savedTopicsSorted.every(
        (savedTopic, index) => savedTopic === currentTopicsSorted[index]
      );

    if (!topicsMatch) {
      setValue('topics', savedTopics, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true
      });
    }
  }, [
    dirtyFields?.topics,
    eventManagerSelected,
    mode,
    normalizedSelectedTopicValues,
    topicsError,
    selectedSourceDetails,
    setValue
  ]);

  // By default, when creating a new source, pre-select all available topics if Event Manager is selected and no topics are already selected
  useEffect(() => {
    if (
      mode !== SourceDialogMode.New ||
      !eventManagerSelected ||
      topicsError ||
      isTopicsLoading ||
      dirtyFields?.topics
    ) {
      return;
    }

    const allTopicIds = availableTopics.map((topic: AcronisTopic) => topic.id);
    if (allTopicIds.length === 0) {
      return;
    }

    const currentTopicsSorted = [...normalizedSelectedTopicValues].sort();
    const allTopicIdsSorted = [...allTopicIds].sort();
    const topicsMatch =
      currentTopicsSorted.length === allTopicIdsSorted.length &&
      allTopicIdsSorted.every(
        (topicId, index) => topicId === currentTopicsSorted[index]
      );

    if (!topicsMatch) {
      setValue('topics', allTopicIds, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true
      });
    }
  }, [
    availableTopics,
    dirtyFields?.topics,
    eventManagerSelected,
    isTopicsLoading,
    mode,
    normalizedSelectedTopicValues,
    setValue,
    topicsError
  ]);

  useEffect(() => {
    if (!eventManagerSelected) {
      const currentTopics = getValues('topics') || [];
      if (currentTopics.length > 0) {
        setValue('topics', [], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  }, [eventManagerSelected, getValues, setValue]);

  useEffect(() => {
    if (!eventManagerSelected || !topicsError) {
      return;
    }

    const currentTopics = getValues('topics') || [];
    if (currentTopics.length > 0) {
      setValue('topics', [], {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true
      });
    }
  }, [eventManagerSelected, getValues, setValue, topicsError]);

  useEffect(() => {
    if (
      !eventManagerSelected ||
      topicsError ||
      mode !== SourceDialogMode.Edit
    ) {
      return;
    }

    const savedTopics =
      (selectedSourceDetails as { topics?: string[] } | undefined)?.topics ||
      [];
    if (savedTopics.length === 0) {
      return;
    }

    // Preserve topics that are no longer available in the API response
    // This allows users to keep their saved selections even if they're deprecated
    const missingTopics = savedTopics.filter(
      (topic: string) =>
        !availableTopics.some(
          (availableTopic: AcronisTopic) =>
            availableTopic.id === topic || availableTopic.name === topic
        )
    );

    if (missingTopics.length > 0) {
      // Merge only the original saved/current selections with missing (deprecated) topics.
      // Do NOT auto-add newly available topics that weren't in the saved list.
      // This preserves user intent and avoids overwriting selections during hydration.
      const mergedTopics = savedTopics;

      // Check if actual change occurred before marking dirty
      const currentTopics = getValues('topics') || [];
      const hasChanged =
        currentTopics.length !== mergedTopics.length ||
        !mergedTopics.every((t) => currentTopics.includes(t));

      if (hasChanged) {
        setValue('topics', mergedTopics, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  }, [
    availableTopics,
    eventManagerSelected,
    mode,
    topicsError,
    selectedSourceDetails,
    setValue
  ]);

  useEffect(() => {
    const topicsFormErrorMessage = errors?.topics?.message as
      | string
      | undefined;
    const shouldClearTopicsApiError =
      eventManagerSelected &&
      !topicsError &&
      topicsFormErrorMessage === TOPICS_API_ERROR_MESSAGE;

    if (shouldClearTopicsApiError) {
      clearErrors('topics');
    }
  }, [clearErrors, errors?.topics?.message, eventManagerSelected, topicsError]);

  useEffect(() => {
    if (!topicsError || isTopicsLoading) {
      lastTopicsErrorRef.current = null;
      return;
    }

    if (lastTopicsErrorRef.current === topicsError) {
      return;
    }

    enqueueSnackbar(TOPICS_API_ERROR_MESSAGE, {
      variant: 'error'
    });
    lastTopicsErrorRef.current = topicsError;
  }, [enqueueSnackbar, isTopicsLoading, topicsError]);

  const allCredentialFieldsFilled =
    Boolean(String(baseUrlValue ?? '').trim()) &&
    Boolean(String(clientIdValue ?? '').trim()) &&
    Boolean(String(clientSecretValue ?? '').trim());

  const shouldShowCredentialRequiredAlert =
    (eventManagerSelected &&
      submitCount > 0 &&
      !isTopicsLoading &&
      availableTopics.length === 0 &&
      !topicsError &&
      !allCredentialFieldsFilled) ||
    false;

  const shouldShowNoTopicsAvailableAlert =
    (eventManagerSelected &&
      !isTopicsLoading &&
      availableTopics.length === 0 &&
      !topicsError &&
      hasSuccessfulTopicsResponse &&
      allCredentialFieldsFilled) ||
    false;

  const previousEventManagerSelectedRef = useRef(eventManagerSelected);

  useEffect(() => {
    const wasEventManagerSelected = previousEventManagerSelectedRef.current;
    const wasJustSelected = !wasEventManagerSelected && eventManagerSelected;

    if (wasJustSelected && allCredentialFieldsFilled) {
      void handleCredentialBlur();
    }

    previousEventManagerSelectedRef.current = eventManagerSelected;
  }, [allCredentialFieldsFilled, eventManagerSelected, handleCredentialBlur]);

  useEffect(() => {
    if (!eventManagerSelected) {
      clearErrors([...ACRONIS_CREDENTIAL_FIELDS]);
      return;
    }

    if (!shouldShowCredentialRequiredAlert) {
      const allCredentialFieldsLoaded = ACRONIS_CREDENTIAL_FIELDS.every(
        (fieldName) => {
          const fieldValue = getValues(fieldName);
          return Boolean(String(fieldValue ?? '').trim());
        }
      );

      if (allCredentialFieldsLoaded) {
        clearErrors([...ACRONIS_CREDENTIAL_FIELDS]);
      }
      return;
    }

    const emptyCredentialFields = ACRONIS_CREDENTIAL_FIELDS.filter(
      (fieldName) => {
        const fieldValue = getValues(fieldName);
        return !String(fieldValue ?? '').trim();
      }
    );

    if (emptyCredentialFields.length === 0) {
      clearErrors([...ACRONIS_CREDENTIAL_FIELDS]);
      return;
    }

    const filledCredentialFields = ACRONIS_CREDENTIAL_FIELDS.filter(
      (fieldName) => !emptyCredentialFields.includes(fieldName)
    );
    if (filledCredentialFields.length > 0) {
      clearErrors([...filledCredentialFields]);
    }

    void trigger(emptyCredentialFields);
  }, [
    clearErrors,
    eventManagerSelected,
    getValues,
    shouldShowCredentialRequiredAlert,
    trigger
  ]);

  // Topics field configuration (only shown when Event Manager is selected)
  const shouldShowSelectAll = mergedTopicOptions.length > 0;
  const topicsFieldConfig = {
    id: 1,
    name: 'topics',
    type: 'autocomplete' as const,
    label: 'Topics',
    value: getValues('topics') || [],
    validations: [],
    validation_type: '',
    options: mergedTopicOptions,
    showSelectAll: shouldShowSelectAll,
    disabled: isTopicsLoading || !eventManagerSelected,
    config: {
      validate: (selected: unknown[]) => {
        if (eventManagerSelected && topicsError) {
          return TOPICS_API_ERROR_MESSAGE;
        }

        // Topics become required only after a successful load attempt.
        if (
          eventManagerSelected &&
          hasSuccessfulTopicsResponse &&
          (!selected || selected.length === 0)
        ) {
          return 'Select at least one Topic when Event Manager is selected';
        }
        return true;
      }
    }
  };

  const topicsErrorMessage =
    (errors?.topics?.message as string | undefined) ||
    (topicsError ? TOPICS_API_ERROR_MESSAGE : undefined);

  return (
    <>
      {/* Topics Multi-Select (conditionally shown when Event Manager is selected) */}
      {eventManagerSelected && (
        <Grid container size={{ xs: 12, sm: 12, md: 12 }}>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <InputLabel htmlFor="topics-input">Topics *</InputLabel>
            <FormControl fullWidth>
              <Box sx={{ position: 'relative' }}>
                {!isTopicsLoading && (
                  <DynamicFormControl
                    {...(topicsFieldConfig as unknown as DynamicFieldData)}
                  />
                )}

                {/* Loading indicator overlay */}
                {isTopicsLoading && (
                  <Box className={classes.fieldLoading}>
                    <Box className={classes.loadingBox}>
                      <CircularProgress color="primary" size={20} />
                    </Box>
                    <Box className={classes.loadingText}>
                      <Typography variant="body2" color="primary">
                        Loading topics...
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            {/* Error message display */}
            {topicsErrorMessage && (
              <ErrorMessage message={topicsErrorMessage} />
            )}

            {/* Help text explaining credential requirement */}
            {shouldShowCredentialRequiredAlert && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Topics will be loaded once you provide valid credentials.
              </Alert>
            )}

            {/* If no topics are available after providing valid credentials, deselect the Event Manager category to proceed. */}
            {shouldShowNoTopicsAvailableAlert && (
              <Alert severity="error" sx={{ mt: 2 }}>
                No topics are available for the Event Manager category. Please
                deselect the Event Manager category to proceed.
              </Alert>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

const AcronisTopics = ({ mode, isResettingSourceType }: AcronisTopicsProps) => {
  if (isResettingSourceType) {
    return null;
  }

  return <AcronisTopicsContent mode={mode} />;
};

export default AcronisTopics;
